'use client';

import { useProductLink } from '@dealerportal/catalog-ui/Hooks/useProductLink';
import type { PaginatedProduct, Swatches } from '@dealerportal/common';
import { formatNumber, useTranslations } from '@dealerportal/i18n/client';
import {
  useClientContext,
  useFavouriteProducts,
} from '@dealerportal/react-helpers';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import { remapBrandName, remapProductName } from '@dealerportal/utilities';
import classNames from 'classnames';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { SAMPLE_ICON_SVG, VISUALIZER_ICON_SVG } from './icons';
import { MarkFavourite } from './MarkFavourite';
import { MissingImage } from './MissingImage';
import { ProductCardSwatches } from './ProductCardSwatches';
import { ProductTags } from './ProductTags';
import { Samples } from './Samples';
import { SpecLabel } from './SpecPill';

interface Props {
  product: PaginatedProduct;
  colorSwatches: Swatches[];
  sampleSwatches: Swatches[];
  onClose: () => void;
}

export const ProductQuickViewDialog: FC<Props> = ({
  product,
  colorSwatches,
  sampleSwatches,
  onClose,
}) => {
  const t = useTranslations('products');
  const tSpecs = useTranslations('specs');
  const dialogRef = useRef<HTMLDialogElement>(null);
  const ctx = useClientContext();
  const { generateProductLink } = useProductLink();
  const favouriteContext = useFavouriteProducts();
  const [hoveredSwatch, setHoveredSwatch] = useState<Swatches | null>(null);
  const currentProduct = hoveredSwatch
    ? { ...product, ...hoveredSwatch }
    : product;

  const handleFavouriteClick = useCallback(() => {
    favouriteContext?.addRemoveProductsFromFav?.(currentProduct);
  }, [currentProduct, favouriteContext]);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const vizToken =
    ctx.visualizerSettings !== undefined
      ? ctx.visualizerSettings.showVisualizer
        ? (ctx.visualizerSettings.apiKey ?? '')
        : ''
      : (ctx.siteTokens.visualizerApiToken ?? '');

  const productLink = generateProductLink(currentProduct);

  return (
    <dialog
      ref={dialogRef}
      className="ui-w-full ui-max-w-4xl ui-m-auto ui-rounded-xl ui-p-4 ui-shadow-2xl backdrop:ui-bg-black/40 ui-border-none !ui-outline-none"
      style={{ backgroundColor: '#F5F7FA', overflow: 'visible' }}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      onClose={onClose}
    >
      <div className="ui-flex ui-flex-col sm:ui-flex-row ui-gap-4">
        {/* Image */}
        <div className="ui-w-full sm:ui-w-1/2 ui-shrink-0 ui-aspect-square ui-self-start ui-bg-gray-50 ui-rounded-lg ui-overflow-hidden">
          {currentProduct.imageURL ? (
            <img
              src={
                currentProduct.imageURL.includes('res.cloudinary.com')
                  ? currentProduct.imageURL.replace(
                      'https://images.floorforce.com/Textures/',
                      ''
                    )
                  : currentProduct.imageURL
              }
              alt={`${currentProduct.productName} by ${currentProduct.brandName}`}
              className="ui-w-full ui-h-full ui-object-cover"
            />
          ) : (
            <MissingImage
              className="ui-fill-gray-400/50 ui-bg-gray-100 ui-w-full ui-h-full"
              width={288}
              height={288}
            />
          )}
        </div>

        {/* Content */}
        <div className="ui-flex ui-flex-col ui-flex-1 ui-gap-4">
          {/* Title row with close button */}
          <div className="ui-flex ui-items-start ui-justify-between ui-gap-2">
            <div>
              <div className="ui-text-xs ui-font-semibold ui-text-gray-500 ui-uppercase">
                {remapBrandName(currentProduct.brandName)}
              </div>
              <div className="ui-text-lg ui-font-bold ui-text-gray-900 ui-mt-1">
                {remapProductName(currentProduct.productName)}
              </div>
              <div className="ui-text-xs ui-text-gray-600 ui-mt-0.5">
                {currentProduct.colorName}
              </div>
              {currentProduct.additionalMessage && (
                <div className="ui-text-xs ui-text-gray-500 ui-mt-1">
                  {currentProduct.additionalMessage}
                </div>
              )}
            </div>
            <BetterButton
              btnStyle="simple"
              className="!ui-outline-none"
              icon='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path class="ui-fill-gray-600" d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z"/></svg>'
              attributes={{
                type: 'button',
                onClick: onClose,
                onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
                'aria-label': t('closeQuickView'),
              }}
            />
          </div>

          {/* Product Tags */}
          {(() => {
            const tags = (currentProduct.tags ?? []).filter(
              (t) => t !== 'Order_Sample'
            );
            return tags.length > 0 ? (
              <div className="ui-flex ui-flex-wrap ui-gap-1">
                <ProductTags tags={tags} isOpen={true} />
              </div>
            ) : null;
          })()}

          {/* Swatches */}
          {colorSwatches.length > 1 && (
            <div>
              <div className="ui-text-xs ui-font-semibold ui-text-gray-500 ui-mb-2">
                <span className="ui-uppercase">{t('colors')} </span>
                <span className="ui-font-normal">
                  {t('colorOptions', { count: colorSwatches.length })}
                </span>
              </div>
              <ProductCardSwatches
                swatches={colorSwatches}
                selectedSwatch={currentProduct}
                onMouseEnter={(swatch) => setHoveredSwatch(swatch)}
                onSelect={() => {}}
                itemClassName="ui-h-[26px] ui-w-[26px] ui-rounded-md"
                listClassName="ui-flex ui-flex-wrap ui-gap-2 ui-list-none ui-p-0 ui-m-0"
              />
            </div>
          )}

          {/* Specs */}
          {(currentProduct.acRating ||
            currentProduct.wearLayer ||
            currentProduct.material ||
            (currentProduct.thickness ?? 0) > 0) && (
            <div className="ui-grid ui-grid-cols-2 ui-gap-2">
              {currentProduct.acRating && (
                <div className="ui-bg-white ui-rounded-lg ui-px-3 ui-py-2.5">
                  <div className="ui-text-xs ui-text-gray-500 ui-mb-0.5">
                    <SpecLabel specId="acRating" />
                  </div>
                  <div className="ui-text-sm ui-font-semibold ui-text-gray-900">
                    {tSpecs('acRatingPrefix')} {currentProduct.acRating}
                  </div>
                </div>
              )}
              {currentProduct.wearLayer && (
                <div className="ui-bg-white ui-rounded-lg ui-px-3 ui-py-2.5">
                  <div className="ui-text-xs ui-text-gray-500 ui-mb-0.5">
                    <SpecLabel specId="wearLayerThickness" />
                  </div>
                  <div className="ui-text-sm ui-font-semibold ui-text-gray-900">
                    {currentProduct.wearLayer} {tSpecs('wearLayerSuffix')}
                  </div>
                </div>
              )}
              {currentProduct.material && (
                <div className="ui-bg-white ui-rounded-lg ui-px-3 ui-py-2.5">
                  <div className="ui-text-xs ui-text-gray-500 ui-mb-0.5">
                    <SpecLabel specId="material" />
                  </div>
                  <div className="ui-text-sm ui-font-semibold ui-text-gray-900">
                    {Array.isArray(currentProduct.material)
                      ? currentProduct.material.join(', ')
                      : currentProduct.material}
                  </div>
                </div>
              )}
              {(currentProduct.thickness ?? 0) > 0 && (
                <div className="ui-bg-white ui-rounded-lg ui-px-3 ui-py-2.5">
                  <div className="ui-text-xs ui-text-gray-500 ui-mb-0.5">
                    <SpecLabel specId="thickness" />
                  </div>
                  <div className="ui-text-sm ui-font-semibold ui-text-gray-900">
                    {formatNumber(currentProduct.thickness ?? 0)}
                    {tSpecs('thicknessSuffix')}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Price */}
          {currentProduct.unitPrice > 0 && currentProduct.priceDescription && (
            <div className="ui-flex ui-items-center ui-gap-1.5">
              <span className="ui-text-2xl ui-font-bold ui-text-gray-900">
                {`$${currentProduct.unitPrice.toFixed(2)}`}
              </span>
              <span className="ui-text-sm ui-text-gray-600">
                / {currentProduct.priceDescription}
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="ui-flex ui-flex-col ui-gap-2 ui-mt-auto">
            {sampleSwatches.length > 0 && (
              <div>
                {sampleSwatches.map((swatch) => (
                  <Samples
                    key={swatch.UPID}
                    samplePrice={0}
                    productInfo={swatch}
                    visible={currentProduct.UPID === swatch.UPID}
                    isLoading={false}
                    icon={SAMPLE_ICON_SVG}
                    replaceClass={{
                      '!ui-w-max': '!ui-w-full',
                      'ui-rounded-md': 'ui-rounded-xl',
                      'ui-h-12': 'ui-h-10',
                    }}
                  />
                ))}
              </div>
            )}

            {currentProduct.hasVisualization && vizToken && (
              <div className="ui-rounded-lg">
                <BetterButton
                  btnStyle="neutral"
                  replaceClass={{
                    '!ui-w-max': '!ui-w-full',
                    'ui-rounded-md': 'ui-rounded-xl',
                    'ui-h-12': 'ui-h-10',
                  }}
                  href={`${productLink}#vis`}
                  label={t('seeInYourRoom').toUpperCase()}
                  icon={VISUALIZER_ICON_SVG}
                  iconSize="4"
                />
              </div>
            )}

            <div className="ui-flex ui-items-center ui-gap-2">
              {favouriteContext && (
                <div className="ui-rounded-md ui-shrink-0">
                  <MarkFavourite
                    isFavourite={Boolean(
                      favouriteContext.find({ UPID: currentProduct.UPID })
                    )}
                    onClick={handleFavouriteClick}
                    btnClass="!ui-min-w-10 !ui-min-h-10 !ui-w-10 !ui-h-10 ui-shrink-0 !ui-border-0 !ui-rounded-md"
                    iconClass="!ui-w-5 !ui-h-5"
                  />
                </div>
              )}
              <div className="ui-rounded-xl ui-flex-1">
                <BetterButton
                  btnStyle="neutral"
                  href={productLink}
                  label={
                    <>
                      <span className="ui-pr-2">{t('viewFullDetails')}</span>
                      <svg
                        className="ui-w-4 ui-shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12.2929 4.29289C12.6834 3.90237 13.3166 3.90237 13.7071 4.29289L20.7071 11.2929C21.0976 11.6834 21.0976 12.3166 20.7071 12.7071L13.7071 19.7071C13.3166 20.0976 12.6834 20.0976 12.2929 19.7071C11.9024 19.3166 11.9024 18.6834 12.2929 18.2929L17.5858 13H4C3.44772 13 3 12.5523 3 12C3 11.4477 3.44772 11 4 11H17.5858L12.2929 5.70711C11.9024 5.31658 11.9024 4.68342 12.2929 4.29289Z"
                          fill="currentColor"
                        />
                      </svg>
                    </>
                  }
                  replaceClass={{
                    '!ui-w-max': '!ui-w-full',
                    '!ui-border-gray-700': '!ui-border-0',
                    'ui-rounded-md': 'ui-rounded-xl',
                    'ui-h-12': 'ui-h-10',
                  }}
                  className={classNames({
                    'ui-hidden': currentProduct.hasVisualization && vizToken,
                  })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </dialog>
  );
};
